import { Suspense, useEffect, useRef } from "react"
import { DateRange } from "react-day-picker"
import { useTranslation } from "react-i18next"
import { PreloadedQuery } from "react-relay"
import { LoadingSpinner } from "@/components/loading-spinner"
import { ScrollArea } from "@/components/ui/scroll-area"
import { DateOnlyFormat } from "@/lib/date-only"
import { addDaysToDate, formatDate } from "@/lib/format-date"
import { HistoryDateRangeFilter } from "@/modules/patients/history/history-date-range-filter"
import { transformAuditEventsToTimeline } from "@/modules/patients/history/libs/transform-audit-events-to-timeline"
import {
  AuditEventsQueryType,
  useAuditEventsPreloadQuery,
  useAuditEventsQuery,
} from "@/modules/patients/history/services/useAuditEventsQuery"
import { TimelineGroup } from "@/modules/patients/history/timeline-group"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"

type HistoryDataProps = {
  queryRef: PreloadedQuery<AuditEventsQueryType>
  countryCode: ReturnType<typeof useCountryParam>
  onRefetchAuditEvents: (from: string, to: string) => void
}
const HistoryData = (props: HistoryDataProps) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()
  const { auditEvents } = useAuditEventsPreloadQuery(props.countryCode, props.queryRef)
  const timelineData = transformAuditEventsToTimeline(auditEvents, t, language)

  return (
    <div className="flex-1 overflow-hidden">
      <ScrollArea viewportClassName="max-h-none" className="h-full">
        <div data-testid="event-details" className="space-y-4 pb-8">
          {timelineData.map((yearEntry, idx) => (
            <TimelineGroup key={yearEntry.year} yearEntry={yearEntry} isFirstGroup={idx === 0} />
          ))}
        </div>
      </ScrollArea>
    </div>
  )
}

const History = () => {
  const patientId = usePatientParam()
  const countryCode = useCountryParam()

  const { t } = useTranslation()
  const { loadQuery, queryRef } = useAuditEventsQuery(countryCode)

  const initializeFilter: DateRange = useRef({ from: addDaysToDate(new Date(), -30), to: new Date() }).current

  const onRefetchAuditEvents = (fromDate: string, toDate: string) => {
    loadQuery({ patientId, fromDate, toDate }, { fetchPolicy: "store-and-network" })
  }

  useEffect(
    () =>
      loadQuery(
        {
          patientId,
          fromDate: formatDate(initializeFilter.from, DateOnlyFormat),
          toDate: formatDate(initializeFilter.to, DateOnlyFormat),
        },
        { fetchPolicy: "store-and-network" },
      ),
    [initializeFilter, loadQuery, patientId],
  )

  return (
    <div className="flex">
      <div className="flex grow flex-col">
        <p
          data-testid="history-title"
          className="bg-surface text-on-surface flex h-16 w-full shrink-0 items-center justify-between border-b px-8 py-3 text-xl font-bold">
          {t("patient.history.history")}
        </p>
        <div className="flex flex-1 flex-col overflow-hidden px-8 pt-8">
          {/* Filter */}
          <div className="mb-8 h-12">
            <HistoryDateRangeFilter initializeFilter={initializeFilter} onFilterChange={onRefetchAuditEvents} />
          </div>
          <Suspense fallback={<LoadingSpinner isFullPage size={32} />}>
            {queryRef && (
              <HistoryData countryCode={countryCode} queryRef={queryRef} onRefetchAuditEvents={onRefetchAuditEvents} />
            )}
          </Suspense>
        </div>
      </div>
    </div>
  )
}

export { History }
